/**
 * Fadelessart 3D Preview Engine
 * Uses Three.js to render a 3D canvas/frame preview
 * Dependencies: Three.js r160 (loaded via CDN in theme.liquid)
 */
(function() {
  'use strict';

  let scene, camera, renderer, canvasMesh, frameMesh;
  let animationId = null;
  let isDragging = false;
  let prevMouse = { x: 0, y: 0 };
  let rotation = { x: -0.15, y: 0 };
  let targetRotation = { x: -0.15, y: 0 };
  let zoom = 4.2;
  let containerEl = null;

  const FRAME_COLORS = {
    black: 0x1a1a1a,
    white: 0xf5f5f5,
    espresso: 0x3e2723,
    natural: 0xc4a882,
    gold: 0xd4af37,
    silver: 0xc0c0c0
  };

  const FRAME_WIDTHS = {
    none: 0,
    black: 0.15,
    white: 0.15,
    espresso: 0.18,
    natural: 0.18,
    gold: 0.22,
    silver: 0.22
  };

  const SIZE_RATIOS = {
    '8x10': [0.8, 1.0],
    '11x14': [0.786, 1.0],
    '16x20': [0.8, 1.0],
    '18x24': [0.75, 1.0],
    '24x36': [0.667, 1.0],
    '30x40': [0.75, 1.0],
    '36x54': [0.667, 1.0]
  };

  function init(options) {
    const canvasEl = options.canvas;
    if (!canvasEl || typeof THREE === 'undefined') {
      console.warn('Three.js not loaded or canvas element missing');
      return;
    }

    // Reference to the surrounding container (used for sizing + resize)
    // Prefer explicit stageEl param so we size to the visible 3D box, not the outer wrap
    containerEl = options.stageEl || canvasEl.parentElement;

    // Clean up previous instance
    destroy();

    // Scene setup
    scene = new THREE.Scene();
    // Brighter neutral background so the canvas reads as the focus, not a dark frame
    scene.background = new THREE.Color(0xfaf8f3);

    // Camera — use container aspect so 3D view matches the 2D preview size
    const w = containerEl.clientWidth || canvasEl.clientWidth;
    const h = containerEl.clientHeight || canvasEl.clientHeight;
    camera = new THREE.PerspectiveCamera(38, w / h, 0.1, 100);
    camera.position.set(0, 0, zoom);

    // Renderer
    renderer = new THREE.WebGLRenderer({ canvas: canvasEl, antialias: true });
    renderer.setSize(w, h, false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;

    // === LIGHTING — brighter, more even, no harsh contrast ===
    // Strong ambient fills shadows so the canvas face never goes dark
    const ambient = new THREE.AmbientLight(0xffffff, 0.95);
    scene.add(ambient);

    // Hemisphere gives a soft sky/ground tint, mimicking a photo studio
    const hemi = new THREE.HemisphereLight(0xfff8ec, 0xe6e0d4, 0.85);
    hemi.position.set(0, 5, 0);
    scene.add(hemi);

    // Key light from top-right, softer than before
    const keyLight = new THREE.DirectionalLight(0xffffff, 1.05);
    keyLight.position.set(2, 3, 4);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.width = 1024;
    keyLight.shadow.mapSize.height = 1024;
    keyLight.shadow.bias = -0.0005;
    scene.add(keyLight);

    // Fill light from the opposite side — doubles the previous value to eliminate the dark side
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.65);
    fillLight.position.set(-2.5, 1.5, 2.5);
    scene.add(fillLight);

    // A frontal rim light so the canvas face is always well-lit when rotated
    const rim = new THREE.DirectionalLight(0xffffff, 0.4);
    rim.position.set(0, 0, 5);
    scene.add(rim);

    // Ground (for shadow)
    const groundGeo = new THREE.PlaneGeometry(20, 20);
    const groundMat = new THREE.ShadowMaterial({ opacity: 0.18 });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -1.5;
    ground.receiveShadow = true;
    scene.add(ground);

    // Create canvas with image
    createCanvasMesh(options.imageSrc, options.size || '11x14', options.frame || 'none');

    // Interaction
    setupInteraction(canvasEl);

    // Resize handler
    window.addEventListener('resize', onResize);

    // Start render loop
    animate();
  }

  function createCanvasMesh(imageSrc, size, frameType) {
    // Remove existing meshes
    if (canvasMesh) {
      scene.remove(canvasMesh);
      canvasMesh.geometry.dispose();
      if (canvasMesh.material.map) canvasMesh.material.map.dispose();
      canvasMesh.material.dispose();
    }
    if (frameMesh) {
      scene.remove(frameMesh);
      frameMesh.geometry.dispose();
      frameMesh.material.dispose();
    }

    const ratio = SIZE_RATIOS[size] || [0.8, 1.0];
    const width = ratio[0] * 2;
    const height = ratio[1] * 2;
    const depth = 0.1;

    // Canvas (image) mesh
    const canvasGeo = new THREE.BoxGeometry(width, height, depth);
    let canvasMat;

    if (imageSrc) {
      const texture = new THREE.TextureLoader().load(imageSrc, (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
      });
      canvasMat = new THREE.MeshStandardMaterial({
        map: texture,
        roughness: 0.6,
        metalness: 0.0
      });
    } else {
      canvasMat = new THREE.MeshStandardMaterial({
        color: 0xeeeeee,
        roughness: 0.8,
        metalness: 0.0
      });
    }

    canvasMesh = new THREE.Mesh(canvasGeo, canvasMat);
    canvasMesh.castShadow = true;
    canvasMesh.receiveShadow = true;
    scene.add(canvasMesh);

    // Frame mesh (if frame is selected)
    if (frameType !== 'none') {
      createFrameMesh(width, height, depth, frameType);
    }
  }

  function createFrameMesh(canvasWidth, canvasHeight, canvasDepth, frameType) {
    const frameWidth = FRAME_WIDTHS[frameType] || 0.15;
    const frameColor = FRAME_COLORS[frameType] || 0x1a1a1a;
    const totalWidth = canvasWidth + frameWidth * 2;
    const totalHeight = canvasHeight + frameWidth * 2;

    // Create frame using 4 boxes
    const frameGroup = new THREE.Group();
    const frameMat = new THREE.MeshStandardMaterial({
      color: frameColor,
      roughness: 0.4,
      metalness: frameType === 'gold' || frameType === 'silver' ? 0.7 : 0.1
    });

    // Top
    const topGeo = new THREE.BoxGeometry(totalWidth, frameWidth, canvasDepth * 1.2);
    const top = new THREE.Mesh(topGeo, frameMat);
    top.position.set(0, canvasHeight / 2 + frameWidth / 2, 0);
    top.castShadow = true;
    frameGroup.add(top);

    // Bottom
    const bottom = top.clone();
    bottom.position.y = -canvasHeight / 2 - frameWidth / 2;
    frameGroup.add(bottom);

    // Left
    const sideGeo = new THREE.BoxGeometry(frameWidth, canvasHeight, canvasDepth * 1.2);
    const left = new THREE.Mesh(sideGeo, frameMat);
    left.position.set(-canvasWidth / 2 - frameWidth / 2, 0, 0);
    left.castShadow = true;
    frameGroup.add(left);

    // Right
    const right = left.clone();
    right.position.x = canvasWidth / 2 + frameWidth / 2;
    frameGroup.add(right);

    frameMesh = frameGroup;
    frameMesh.position.z = canvasDepth / 2;
    scene.add(frameMesh);
  }

  function setupInteraction(canvasEl) {
    canvasEl.addEventListener('mousedown', (e) => {
      isDragging = true;
      prevMouse = { x: e.clientX, y: e.clientY };
      canvasEl.style.cursor = 'grabbing';
    });

    canvasEl.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - prevMouse.x;
      const deltaY = e.clientY - prevMouse.y;
      targetRotation.y += deltaX * 0.01;
      targetRotation.x += deltaY * 0.01;
      targetRotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, targetRotation.x));
      prevMouse = { x: e.clientX, y: e.clientY };
    });

    canvasEl.addEventListener('mouseup', () => {
      isDragging = false;
      canvasEl.style.cursor = 'grab';
    });

    canvasEl.addEventListener('mouseleave', () => {
      isDragging = false;
      canvasEl.style.cursor = 'grab';
    });

    canvasEl.addEventListener('wheel', (e) => {
      e.preventDefault();
      zoom += e.deltaY * 0.003;
      zoom = Math.max(3, Math.min(10, zoom));
      camera.position.z = zoom;
    });

    // Touch support
    canvasEl.addEventListener('touchstart', (e) => {
      if (e.touches.length === 1) {
        isDragging = true;
        prevMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    });

    canvasEl.addEventListener('touchmove', (e) => {
      if (!isDragging || e.touches.length !== 1) return;
      e.preventDefault();
      const deltaX = e.touches[0].clientX - prevMouse.x;
      const deltaY = e.touches[0].clientY - prevMouse.y;
      targetRotation.y += deltaX * 0.01;
      targetRotation.x += deltaY * 0.01;
      targetRotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, targetRotation.x));
      prevMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }, { passive: false });

    canvasEl.addEventListener('touchend', () => {
      isDragging = false;
    });

    canvasEl.style.cursor = 'grab';
  }

  function animate() {
    animationId = requestAnimationFrame(animate);

    // Smooth rotation
    rotation.x += (targetRotation.x - rotation.x) * 0.1;
    rotation.y += (targetRotation.y - rotation.y) * 0.1;

    // Auto-rotate when not dragging
    if (!isDragging) {
      targetRotation.y += 0.002;
    }

    if (canvasMesh) {
      canvasMesh.rotation.x = rotation.x;
      canvasMesh.rotation.y = rotation.y;
    }
    if (frameMesh) {
      frameMesh.rotation.x = rotation.x;
      frameMesh.rotation.y = rotation.y;
    }

    renderer.render(scene, camera);
  }

  function onResize() {
    if (!renderer || !camera || !containerEl) return;
    const w = containerEl.clientWidth;
    const h = containerEl.clientHeight;
    if (w === 0 || h === 0) return;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
  }

  function updateFrame(frameType) {
    if (!scene || !canvasMesh) return;
    const options = canvasMesh.userData || {};
    createCanvasMesh(options.imageSrc || null, options.size || '11x14', frameType);
  }

  function updateSize(size) {
    if (!scene || !canvasMesh) return;
    const options = canvasMesh.userData || {};
    createCanvasMesh(options.imageSrc || null, size, options.frame || 'none');
  }

  function updateImage(imageSrc) {
    if (!scene || !canvasMesh) return;
    const options = canvasMesh.userData || {};
    createCanvasMesh(imageSrc, options.size || '11x14', options.frame || 'none');
  }

  function destroy() {
    if (animationId) {
      cancelAnimationFrame(animationId);
      animationId = null;
    }
    if (renderer) {
      renderer.dispose();
      renderer = null;
    }
    if (canvasMesh) {
      if (canvasMesh.geometry) canvasMesh.geometry.dispose();
      if (canvasMesh.material) {
        if (canvasMesh.material.map) canvasMesh.material.map.dispose();
        canvasMesh.material.dispose();
      }
      canvasMesh = null;
    }
    if (frameMesh) {
      frameMesh.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      frameMesh = null;
    }
    scene = null;
    camera = null;
    window.removeEventListener('resize', onResize);
  }

  // Export
  window.Fadelessart3D = {
    init,
    destroy,
    updateFrame,
    updateSize,
    updateImage
  };
})();
